import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/consumer/lib/api'
import type { ConsumerFieldMeta } from '@/consumer/hooks/use-registration'
import type { ConsentState } from '@/shared/components/consumer-consent-kinds'

export interface ConsumerSession {
  uuid: string
  email: string
  first_name: string
  last_name: string
  phone_number: string
  /** ISO timestamp of T&C / Privacy Policy acceptance, or null if not yet accepted. */
  accepted_terms_at: string | null
  needs_onboarding: boolean
  has_usable_password: boolean
  /**
   * The consumer's own configurable personal attributes (e.g. the composite
   * ADDRESS field) for the active tenant, and their current values keyed by
   * code_name. Empty when no tenant is resolvable. Drive the editable settings
   * fields on the Account page.
   */
  consumer_fields: ConsumerFieldMeta[]
  consumer_attribute_values: Record<string, unknown>
  /** This site's tenant: the consumer's consent choices. */
  consents?: ConsentState
}

export function useConsumerSession() {
  return useQuery<ConsumerSession>({
    queryKey: ['consumer-session'],
    queryFn: () => api.get('/me/').then((r) => r.data),
    retry: 1,
    staleTime: 30_000,
  })
}

export interface ConsumerProfileUpdatePayload {
  first_name?: string
  last_name?: string
  phone_number?: string
  password?: string
  /** When true, records the consumer's T&C / Privacy Policy acceptance. */
  accept_terms?: boolean
  /** Values for the consumer's configurable personal attributes, by code_name. */
  consumer_attribute_values?: Record<string, unknown>
  /** The consumer's own marketing email choice for this site's tenant. */
  consents?: { marketing_email: boolean }
}

export function useUpdateConsumerProfile() {
  const queryClient = useQueryClient()
  return useMutation<ConsumerSession, unknown, ConsumerProfileUpdatePayload>({
    mutationFn: (payload) => api.patch('/me/', payload).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['consumer-session'], data)
      queryClient.invalidateQueries({ queryKey: ['consumer-session'] })
    },
  })
}
